import noUiSlider from "nouislider";
import { v4 as uuidv4 } from "uuid";



import { Factory } from "@/data/dao/Factory";
import { type IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
import { TableDao } from "@/data/dao/service/TableDao";
import { OperatorsCanBreakReservationRules, OperatorsMustAcceptOrDeclineReservation, RefundabilityType, Weekday } from "@/data/model/Enums";
import type { IExcludedTableOrCombination, IScheduleShift } from "@/data/model/OfferSchedule";
import type { IReservationTablesToAssign } from "@/data/model/Order";
import type { ILinkableEntity } from "@/data/model/Shared";
import { DateTime } from "@/data/util/DateTime";
import { Misc } from "@/data/util/Misc";



import { type VueComponentInstance, createVueViewComponent } from "@/shared/utils/vue";



import { MultiEntityInput } from "@/components/formControls/MultiEntityInput";



import { i18n } from "@/portal/Trans";
import DurationPicker from "@/portal/components/elements/DurationPicker.vue";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { DomUtils } from "@/portal/helpers/DomUtils";


export class Shift {
    private readonly $element: JQuery;
    private $wholeDayCheck: JQuery;
    private weekdaySelect: Select<Weekday>;
    private fromTimePicker: TimePicker;
    private untilTimePicker: TimePicker;
    private preOrderTimePicker: TimePicker;
    private fulfilmentTimeIntervalTimePicker: TimePicker;
    private defaultCapacityPerFulfilmentNumberInput: NumberInput;
    private $nameField: JQuery;
    private readonly id: string;
    private operatorsMayEditOrdersSwitch: Switch;
    private editOrderUpToFulfillmentTimeTimePicker: VueComponentInstance<typeof DurationPicker>;
    private readonly $editOrderUpToFulfillmentTimeRow: JQuery;

    private endUsersMayCancelOrdersSwitch: Switch;
    private cancelOrderUpToFulfillmentTimeTimePicker: VueComponentInstance<typeof DurationPicker>;
    private readonly $cancelOrderUpToFulfillmentTimeRow: JQuery;
    private excludedTablesMultiselectList: MultiEntityInput;

    private refundabilityTypeSelect: Select<RefundabilityType>;
    private tableDao: TableDao = Factory.getInstance<TableDao>(TableDao);
    private currentTablesOrCombinationsToExclude?: IReservationTablesToAssign;

    private capacityAdjustmentPercentageSlider: any;
    private $capacityAdjustmentPercentageField: JQuery;

    private autoAcceptUnassignedReservationsSwitch: Switch;

    private operatorsMustAcceptOrDeclineReservationSelect: Select<OperatorsMustAcceptOrDeclineReservation>;
    private operatorsCanBreakReservationRulesSelect: Select<OperatorsCanBreakReservationRules>;

    private readonly $overbookingAboveThresholdRow: JQuery;
    private overbookingAboveThresholdField: NumberInput;

    private $messageToUserAfterPendingOperatorReservationRow: JQuery;
    private messageToUserAfterPendingOperatorReservationField: MultiLanguageInput;

    private expectedLostSeatRatioSlider: any;
    private $expectedLostSeatRatioField: JQuery;

    private tablesDataSource: IMultiEntityLinkDataSource = {
        search: async (excludedIds, term) => {
            const getTablesResult = await this.tableDao.getAvailableTablesOrCombinations({});
            this.currentTablesOrCombinationsToExclude = getTablesResult;

            const tableEntities: ILinkableEntity[] = getTablesResult.tables.map((t) => {
                return {
                    id: t.tableId,
                    order: undefined,
                    name: t.tableName,
                };
            });

            const combinationEntities: Array<ILinkableEntity> = getTablesResult.combinations.map((t) => {
                return {
                    id: t.combinationId,
                    order: undefined,
                    name: t.tableNames,
                };
            });

            const entities: ILinkableEntity[] = tableEntities.concat(combinationEntities);
            return entities.filter(
                (x) => !excludedIds.includes(x.id) && x.name.toLowerCase().includes(term?.toLowerCase() ?? "")
            );
        },
    };

    public onRemoved?: { () };
    public onClone?: { (shift: IScheduleShift) };

    constructor(model?: IScheduleShift) {
        this.id = model?.id ?? uuidv4();

        this.$element = $('<div class="shift" />').data("ctrl", this);

        const nameRow = $('<div class="row name" />').appendTo(this.$element);
        nameRow.append('<div class="col-3">' + i18n("general.fields.name") + ' <span class="mandatory">*</span></div>');
        const nameCol = $(
            '<div class="col-9 name" ><input type="text" name="name" class="form-control" /></div>'
        ).appendTo(nameRow);

        this.$nameField = nameCol.find('.form-control[name="name"]');
        this.$nameField.val(model?.name ?? "");
        let row = $('<div class="row weekday" />').appendTo(this.$element);
        row.append(
            '<div class="col-3">' + i18n("general.timeFormat.weekdays") + ' <span class="mandatory">*</span></div>'
        );
        let col = $('<div class="col-9 weekdays" />').appendTo(row);

        const weekSel = $("<select />").appendTo(col);
        Misc.eachEnum(Weekday, (i, e) => {
            weekSel.append('<option value="' + i + '">' + i18n("enums.Weekday." + e) + " </option>");
        });

        this.weekdaySelect = Select.createFromSelect(
            weekSel,
            undefined,
            undefined,
            false,
            i18n("general.all"),
            undefined,
            true
        );
        this.weekdaySelect.setValues(model !== undefined && model.weekdays.length !== 7 ? model.weekdays : ["none"]);

        row = $('<div class="row time" />').appendTo(this.$element);
        row.append('<div class="col-3">' + i18n("general.fields.time") + ' <span class="mandatory">*</span></div>');
        col = $('<div class="col-9" />').appendTo(row);
        this.$wholeDayCheck = $(
            '<label class="wholedaycheck"><input type="checkbox" /> ' + i18n("general.timeFormat.wholeDay") + "</label>"
        )
            .appendTo(col)
            .find("> input");
        if (model === undefined || model.wholeDay) this.$wholeDayCheck.prop("checked", true);

        this.$wholeDayCheck.change(() => this.onChangeWholeDay());
        col.append("<span>" + i18n("general.timeFormat.from") + "</span>");
        this.fromTimePicker = new TimePicker(undefined, { showSeconds: false, maxHours: 24 }).renderTo(col);
        this.fromTimePicker.setValue(model === undefined ? undefined : model.from);

        col.append("<span>" + i18n("general.timeFormat.until") + "</span>");
        this.untilTimePicker = new TimePicker(undefined, {
            showSeconds: false,
            maxHours: 24,
            allowMidnight: true,
        }).renderTo(col);
        this.untilTimePicker.setValue(model === undefined ? undefined : model.until);

        // Header
        $(
            '<h3 class="kt-section__title kt-section__title-sm mt-3">' +
                i18n("elements.offerSchedulesEditor.preOrders") +
                "</h3>"
        ).appendTo(this.$element);

        const preOrdersForm = $('<div class="pre-orders-form"></div>').appendTo(this.$element);

        const fulfilmentTimeIntervalRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        fulfilmentTimeIntervalRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.fulfilmentTimeInterval") +
                ' <span class="mandatory">*</span></div>'
        );
        const fulfilmentTimeIntervalCol = $('<div class="col-3" />').appendTo(fulfilmentTimeIntervalRow);
        this.fulfilmentTimeIntervalTimePicker = new TimePicker(
            undefined,
            { showSeconds: false, maxHours: 24, minuteStep: 5 },
            "00:00"
        ).renderTo(fulfilmentTimeIntervalCol);
        this.fulfilmentTimeIntervalTimePicker.setValue(model === undefined ? "0:10" : model.fulfilmentTimeInterval);

        const minPreOrderPeriodRow = $('<div class="row preordertime"></div>').appendTo(preOrdersForm);
        minPreOrderPeriodRow.append(
            '<div class="col-label  col-6">' + i18n("elements.offerSchedulesEditor.preOrdertime") + "</div>"
        );
        const minPreOrderPeriodCol = $('<div class="col-3" />').appendTo(minPreOrderPeriodRow);
        this.preOrderTimePicker = new TimePicker(
            undefined,
            { showSeconds: false, maxHours: 337, minuteStep: 1 },
            "00:00"
        ).renderTo(minPreOrderPeriodCol);
        this.preOrderTimePicker.setValue(
            model === undefined ? "0:10" : DateTime.secondsToTimespan(model.preorderTimeInSeconds!)
        );

        const defaultCapacityPerFulfilmentTimeRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        defaultCapacityPerFulfilmentTimeRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.defaultCapacityPerFulfilmentTime") +
                "</div>"
        );
        const defaultCapacityPerFulfilmentTimeCol = $('<div class="col-3" />').appendTo(
            defaultCapacityPerFulfilmentTimeRow
        );

        this.defaultCapacityPerFulfilmentNumberInput = new NumberInput(
            $('<input type="text" name="default-capacity-per-fulfilment-time" class="form-control"/>').appendTo(
                defaultCapacityPerFulfilmentTimeCol
            )
        );

        this.defaultCapacityPerFulfilmentNumberInput.setValue(model?.defaultCapacityPerFulfilmentTime);

        defaultCapacityPerFulfilmentTimeCol.append(
            `<div class="form-text text-muted">${i18n("elements.offerSchedulesEditor.orders")}</div>`
        );

        // Operator may edit orders
        const operatorMayEditOrdersRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        operatorMayEditOrdersRow.append(
            '<div class="col-label col-6">' + i18n("elements.offerSchedulesEditor.operatorsMayEditOrders") + "</div>"
        );
        const operatorMyEditOrdersCol = $('<div class="col-3" />').appendTo(operatorMayEditOrdersRow);

        this.operatorsMayEditOrdersSwitch = new Switch($('<input type="checkbox"/>').appendTo(operatorMyEditOrdersCol));
        this.operatorsMayEditOrdersSwitch.onChange = (isEnabled: boolean) => {
            this.setEditOrderUpToFulfillmentTimeRowVisibility(isEnabled);
        };

        this.$editOrderUpToFulfillmentTimeRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        this.$editOrderUpToFulfillmentTimeRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.editOrderUpToFulfillmentTime") +
                ' <span class="mandatory">*</span></div>'
        );
        const editOrderUpToFulfillmentTimeCol = $('<div class="col-3" />').appendTo(
            this.$editOrderUpToFulfillmentTimeRow
        );

        this.editOrderUpToFulfillmentTimeTimePicker = createVueViewComponent(
            DurationPicker,
            {
                maxHours: 168,
            },
            editOrderUpToFulfillmentTimeCol[0]
        ).mountedComponent;

        // End users may cancel orders
        const endUsersMayCancelOrdersRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        endUsersMayCancelOrdersRow.append(
            '<div class="col-label col-6">' + i18n("elements.offerSchedulesEditor.endUsersMayCancelOrders") + "</div>"
        );
        const endUsersMyCancelOrdersCol = $('<div class="col-3" />').appendTo(endUsersMayCancelOrdersRow);

        this.endUsersMayCancelOrdersSwitch = new Switch(
            $('<input type="checkbox"/>').appendTo(endUsersMyCancelOrdersCol)
        );
        this.endUsersMayCancelOrdersSwitch.onChange = (isEnabled: boolean) => {
            this.setCancelOrderUpToFulfillmentTimeRowVisibility(isEnabled);
        };

        this.$cancelOrderUpToFulfillmentTimeRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        this.$cancelOrderUpToFulfillmentTimeRow.append(
            `<div class="col-label col-6">${i18n("elements.offerSchedulesEditor.editOrderUpToFulfillmentTime")} <span class="mandatory">*</span></div>`
        );

        const cancelOrderUpToFulfillmentTimeCol = $('<div class="col-3" />').appendTo(
            this.$cancelOrderUpToFulfillmentTimeRow
        );

        this.cancelOrderUpToFulfillmentTimeTimePicker = createVueViewComponent(
            DurationPicker,
            {
                maxHours: 168,
            },
            cancelOrderUpToFulfillmentTimeCol[0]
        ).mountedComponent;

        // Refundability
        const refundabilityRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        refundabilityRow.append(
            '<div class="col-label col-6">' + i18n("elements.offerSchedulesEditor.refundability") + "</div>"
        );
        const refundabilityCol = $('<div class="col-3" />').appendTo(refundabilityRow);
        const refundabilitySelect = $("<select />").appendTo(refundabilityCol);
        Misc.eachEnum(RefundabilityType, (i, e) => {
            if (e !== RefundabilityType[RefundabilityType.RefundableWithFee])
                refundabilitySelect.append(
                    '<option value="' + i + '">' + i18n("enums.PreOrderRefundabilityType." + e) + " </option>"
                );
        });

        this.refundabilityTypeSelect = Select.createFromSelect(
            refundabilitySelect,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            false
        );
        this.refundabilityTypeSelect.setValue(
            model !== undefined ? model.preorderRefundabilityType : RefundabilityType.Refundable
        );

        $(
            '<h3 class="kt-section__title kt-section__title-sm mt-3">' +
                i18n("elements.offerSchedulesEditor.tableReservations") +
                "</h3>"
        ).appendTo(preOrdersForm);

        const $excludedTablesRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        $excludedTablesRow.append(
            `<div class="col-label col-6">${i18n("elements.offerSchedulesEditor.excludeTables")} <span class="mandatory">*</span></div>`
        );

        const $excludedTablesColumn = $('<div class="col-5" />').appendTo($excludedTablesRow);

        this.excludedTablesMultiselectList = MultiEntityInput.createFromInput(
            $('<input type="text" name="child-locations" class="form-control" />').appendTo($excludedTablesColumn),
            {
                dataSource: this.tablesDataSource,
            }
        );

        this.tablesDataSource.search([]).then((data) => {
            const selectedTables = data.filter(
                (t) => model?.excludedTablesOrCombinations.find((et) => et.id === t.id) != null
            );
            this.excludedTablesMultiselectList.setValue(selectedTables);
        });

        const $capacityAdjustmentPercentageRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        $capacityAdjustmentPercentageRow.append(
            `<div class="col-label col-6">${i18n("elements.offerSchedulesEditor.capacityAdjustmentPercentage")} <span class="mandatory">*</span></div>`
        );

        const $capacityAdjustmentPercentageColumn = $('<div class="col-5 d-flex align-items-center" />').appendTo(
            $capacityAdjustmentPercentageRow
        );
        this.capacityAdjustmentPercentageSlider = noUiSlider.create(
            $("<div class='mr-1' style='flex-grow: 2;'></div>").appendTo($capacityAdjustmentPercentageColumn)[0],
            {
                start: [0],
                step: 1,
                format: {
                    to: (value) => Math.trunc(value) + "",
                    from: (value) => parseInt(value),
                },
                range: {
                    min: [-50],
                    max: [50],
                },
            }
        );

        this.$capacityAdjustmentPercentageField = $(
            '<input type="text" name="capacityAdjustmentPercentageField" class="form-control" style="width: 30%" />'
        ).appendTo($capacityAdjustmentPercentageColumn);

        this.$capacityAdjustmentPercentageField.change(() => {
            this.capacityAdjustmentPercentageSlider.set(this.$capacityAdjustmentPercentageField.val());
        });

        this.capacityAdjustmentPercentageSlider.on("update", (values, handle) => {
            this.$capacityAdjustmentPercentageField!.val(values[handle]);

            if (parseInt(values[handle]) >= 1) {
                this.autoAcceptUnassignedReservationsSwitch?.setValue(true);
                this.autoAcceptUnassignedReservationsSwitch?.setDisabled(true);
            } else {
                this.autoAcceptUnassignedReservationsSwitch?.setDisabled(false);
            }
        });

        const $autoAcceptUnassignedReservationsRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        $autoAcceptUnassignedReservationsRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.autoAcceptUnassignedReservations") +
                "</div>"
        );
        const $autoAcceptUnassignedReservationsColumn = $('<div class="col-3" />').appendTo(
            $autoAcceptUnassignedReservationsRow
        );

        this.autoAcceptUnassignedReservationsSwitch = new Switch(
            $('<input type="checkbox"/>').appendTo($autoAcceptUnassignedReservationsColumn)
        );

        this.autoAcceptUnassignedReservationsSwitch.setValue(model?.autoAcceptUnassignedReservations ?? false);

        // OperatorsMustAcceptOrDeclineReservation
        const operatorsMustAcceptOrDeclineReservationRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        operatorsMustAcceptOrDeclineReservationRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.operatorsMustAcceptOrDeclineReservation") +
                "</div>"
        );
        const operatorsMustAcceptOrDeclineReservationColumn = $('<div class="col-3" />').appendTo(
            operatorsMustAcceptOrDeclineReservationRow
        );
        const operatorsMustAcceptOrDeclineReservationSelect = $("<select />").appendTo(
            operatorsMustAcceptOrDeclineReservationColumn
        );
        Misc.eachEnum(OperatorsMustAcceptOrDeclineReservation, (i, e) => {
            operatorsMustAcceptOrDeclineReservationSelect.append(
                '<option value="' + i + '">' + i18n("enums.OperatorsMustAcceptOrDeclineReservation." + e) + " </option>"
            );
        });

        this.$messageToUserAfterPendingOperatorReservationRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        this.$messageToUserAfterPendingOperatorReservationRow.append(
            `<div class="col-label col-6">${i18n("elements.offerSchedulesEditor.messageToUserAfterPendingOperatorReservation")} <span class="mandatory">*</span></div>`
        );

        const $messageToUserAfterPendingOperatorReservationColumn = $('<div class="col-5" />').appendTo(
            this.$messageToUserAfterPendingOperatorReservationRow
        );

        this.messageToUserAfterPendingOperatorReservationField = MultiLanguageInput.fromInput(
            $('<textarea name="messageToUserAfterPendingOperatorReservationField" class="form-control"/>').appendTo(
                $messageToUserAfterPendingOperatorReservationColumn
            )
        );

        this.messageToUserAfterPendingOperatorReservationField.setValues(
            model?.messageToUserAfterPendingOperatorReservation ?? undefined
        );

        this.operatorsMustAcceptOrDeclineReservationSelect = Select.createFromSelect(
            operatorsMustAcceptOrDeclineReservationSelect,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            false
        );

        const operatorMustAcceptOrDeclineReservation =
            model?.operatorsMustAcceptOrDeclineReservation ?? OperatorsMustAcceptOrDeclineReservation.Never;

        this.operatorsMustAcceptOrDeclineReservationSelect.setValue(operatorMustAcceptOrDeclineReservation);

        // OperatorsMustAcceptOrDeclineReservation
        this.$overbookingAboveThresholdRow = $('<div class="mt-2"></div>').appendTo(
            operatorsMustAcceptOrDeclineReservationColumn
        );

        this.overbookingAboveThresholdField = new NumberInput(
            $('<input class="form-control" />').appendTo(this.$overbookingAboveThresholdRow),
            { min: 0 }
        );

        this.overbookingAboveThresholdField.setValue(model?.overbookingAboveThreshold);

        // OperatorsCanBreakReservationRules
        const operatorsCanBreakReservationRulesRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        operatorsCanBreakReservationRulesRow.append(
            '<div class="col-label col-6">' +
                i18n("elements.offerSchedulesEditor.operatorsCanBreakReservationRules") +
                "</div>"
        );
        const operatorsCanBreakReservationRulesColumn = $('<div class="col-3" />').appendTo(
            operatorsCanBreakReservationRulesRow
        );
        const operatorsCanBreakReservationRulesSelect = $("<select />").appendTo(
            operatorsCanBreakReservationRulesColumn
        );
        Misc.eachEnum(OperatorsCanBreakReservationRules, (i, e) => {
            operatorsCanBreakReservationRulesSelect.append(
                '<option value="' + i + '">' + i18n("enums.OperatorsCanBreakReservationRules." + e) + " </option>"
            );
        });

        this.operatorsCanBreakReservationRulesSelect = Select.createFromSelect(
            operatorsCanBreakReservationRulesSelect,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            false
        );

        this.operatorsCanBreakReservationRulesSelect.setValue(
            model !== undefined ? model.operatorsCanBreakReservationRules : OperatorsCanBreakReservationRules.DoNotAllow
        );

        this.operatorsMustAcceptOrDeclineReservationSelect.onChange = (value: string | undefined) => {
            this.setMessageToUserAfterPendingOperatorReservationRowVisibility(
                value != null && parseInt(value) != OperatorsMustAcceptOrDeclineReservation.Never
            );
            this.setOverbookingAboveThresholdRowVisibility(
                value != null && parseInt(value) === OperatorsMustAcceptOrDeclineReservation.OverbookingAbove
            );
        };

        const $expectedLostSeatRatioRow = $('<div class="row"></div>').appendTo(preOrdersForm);
        $expectedLostSeatRatioRow.append(
            `<div class="col-label col-6">${i18n("elements.offerSchedulesEditor.expectedLostSeatRatio")}</div>`
        );

        const $expectedLostSeatRatioColumn = $('<div class="col-5 d-flex align-items-center" />').appendTo(
            $expectedLostSeatRatioRow
        );
        this.expectedLostSeatRatioSlider = noUiSlider.create(
            $("<div class='mr-1' style='flex-grow: 2;'></div>").appendTo($expectedLostSeatRatioColumn)[0],
            {
                start: [13],
                step: 1,
                format: {
                    to: (value) => Math.trunc(value) + "",
                    from: (value) => parseFloat(value),
                },
                range: {
                    min: [10],
                    max: [20],
                },
            }
        );

        this.$expectedLostSeatRatioField = $(
            '<input type="text" name="expectedLostSeatRatioField" class="form-control" style="width: 30%" />'
        ).appendTo($expectedLostSeatRatioColumn);

        this.$expectedLostSeatRatioField.change(() => {
            this.expectedLostSeatRatioSlider.set(this.$expectedLostSeatRatioField.val() * 10);
        });

        this.expectedLostSeatRatioSlider.on("update", (values, handle) => {
            this.$expectedLostSeatRatioField!.val(values[handle] / 10);
        });

        this.expectedLostSeatRatioSlider.set((model?.expectedLostSeatRatio ?? 1.3) * 10);

        const footer = $('<div class="footer"></div>').appendTo(this.$element);
        $('<a href="#delete" class="removebtn">' + i18n("general.delete") + "</a>")
            .appendTo(footer)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.destroy();

                if (this.onRemoved !== undefined) this.onRemoved();
            });

        $('<a href="" class="btn-text mr-2">' + i18n("general.copy") + "</a>")
            .appendTo(footer)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                const shift = this.getValueToClone();

                if (this.onClone != null) {
                    this.onClone(shift);
                }
            });

        this.operatorsMayEditOrdersSwitch.setValue(
            model?.editOrderByOperatorUpToFulfillmentTime != null &&
                model?.editOrderByOperatorUpToFulfillmentTime.length > 0
        );
        this.endUsersMayCancelOrdersSwitch.setValue(
            model?.cancelOrderByEndUserUpToFulfillmentTime != null &&
                model?.cancelOrderByEndUserUpToFulfillmentTime.length > 0
        );

        this.editOrderUpToFulfillmentTimeTimePicker.setDuration(
            model === undefined ? "0:00" : model.editOrderByOperatorUpToFulfillmentTime
        );
        this.cancelOrderUpToFulfillmentTimeTimePicker.setDuration(
            model === undefined ? "0:00" : model.cancelOrderByEndUserUpToFulfillmentTime
        );

        this.setEditOrderUpToFulfillmentTimeRowVisibility(
            model?.editOrderByOperatorUpToFulfillmentTime != null &&
                model?.editOrderByOperatorUpToFulfillmentTime.length > 0
        );
        this.setCancelOrderUpToFulfillmentTimeRowVisibility(
            model?.cancelOrderByEndUserUpToFulfillmentTime != null &&
                model?.cancelOrderByEndUserUpToFulfillmentTime.length > 0
        );

        this.setMessageToUserAfterPendingOperatorReservationRowVisibility(
            operatorMustAcceptOrDeclineReservation != OperatorsMustAcceptOrDeclineReservation.Never
        );
        this.setOverbookingAboveThresholdRowVisibility(
            operatorMustAcceptOrDeclineReservation === OperatorsMustAcceptOrDeclineReservation.OverbookingAbove
        );

        this.capacityAdjustmentPercentageSlider.set(model?.capacityAdjustmentPercentage ?? 0);

        this.onChangeWholeDay();
    }

    private setCancelOrderUpToFulfillmentTimeRowVisibility(setVisible: boolean) {
        if (setVisible) {
            this.$cancelOrderUpToFulfillmentTimeRow.show();
        } else {
            this.$cancelOrderUpToFulfillmentTimeRow.hide();
        }
    }

    private setEditOrderUpToFulfillmentTimeRowVisibility(setVisible: boolean) {
        if (setVisible) {
            this.$editOrderUpToFulfillmentTimeRow.show();
        } else {
            this.$editOrderUpToFulfillmentTimeRow.hide();
        }
    }

    private setOverbookingAboveThresholdRowVisibility(setVisible: boolean) {
        if (setVisible) {
            this.$overbookingAboveThresholdRow.show();
        } else {
            this.$overbookingAboveThresholdRow.hide();
        }
    }

    private setMessageToUserAfterPendingOperatorReservationRowVisibility(setVisible: boolean) {
        if (setVisible) {
            this.$messageToUserAfterPendingOperatorReservationRow.show();
        } else {
            this.$messageToUserAfterPendingOperatorReservationRow.hide();
        }
    }

    private onChangeWholeDay() {
        if (this.$wholeDayCheck.is(":checked")) {
            this.fromTimePicker.reset().setDisabled(true);
            this.untilTimePicker.reset().setDisabled(true);
        } else {
            this.fromTimePicker.setDisabled(false);
            this.untilTimePicker.setDisabled(false);
        }
    }

    public appendTo($element: JQuery): Shift {
        this.$element.appendTo($element);
        return this;
    }

    public destroy() {
        this.$element.remove();
    }

    public validateOrValue(): boolean | IScheduleShift {
        let hasError = false;

        this.operatorsMustAcceptOrDeclineReservationSelect.setError(false);
        this.overbookingAboveThresholdField.setError(false);
        this.messageToUserAfterPendingOperatorReservationField.setError(false);
        this.$capacityAdjustmentPercentageField.removeClass("is-invalid");

        const operatorsMustAcceptOrDeclineReservation = parseInt(
            this.operatorsMustAcceptOrDeclineReservationSelect.getValue() ?? "-1"
        );

        if (operatorsMustAcceptOrDeclineReservation === -1) {
            hasError = true;
            this.operatorsMustAcceptOrDeclineReservationSelect.setError(true);
        } else {
            if (operatorsMustAcceptOrDeclineReservation === OperatorsMustAcceptOrDeclineReservation.OverbookingAbove) {
                const overbookingAboveThreshold = this.overbookingAboveThresholdField.getValue();
                const capacityAdjustmentPercentage = parseInt(this.capacityAdjustmentPercentageSlider.get());

                if (capacityAdjustmentPercentage <= 0) {
                    hasError = true;
                    this.$capacityAdjustmentPercentageField.addClass("is-invalid");
                }

                if (overbookingAboveThreshold == null) {
                    hasError = true;
                    this.overbookingAboveThresholdField.setError(true);
                } else if (overbookingAboveThreshold < 0) {
                    hasError = true;
                    this.overbookingAboveThresholdField.setError(true);
                } else if (overbookingAboveThreshold >= capacityAdjustmentPercentage) {
                    hasError = true;
                    this.overbookingAboveThresholdField.setError(true);
                }
            }

            if (operatorsMustAcceptOrDeclineReservation !== OperatorsMustAcceptOrDeclineReservation.Never) {
                const messageToUserAfterPendingOperatorReservation =
                    this.messageToUserAfterPendingOperatorReservationField.getValues();
                if (
                    (messageToUserAfterPendingOperatorReservation.en == null ||
                        messageToUserAfterPendingOperatorReservation.en.length === 0) &&
                    (messageToUserAfterPendingOperatorReservation.de == null ||
                        messageToUserAfterPendingOperatorReservation.de.length === 0) &&
                    (messageToUserAfterPendingOperatorReservation.fr == null ||
                        messageToUserAfterPendingOperatorReservation.fr.length === 0) &&
                    (messageToUserAfterPendingOperatorReservation.it == null ||
                        messageToUserAfterPendingOperatorReservation.it.length === 0)
                ) {
                    hasError = true;
                    this.messageToUserAfterPendingOperatorReservationField.setError(true);
                }
            }
        }

        this.operatorsCanBreakReservationRulesSelect.setError(false);
        if (this.operatorsCanBreakReservationRulesSelect.getValue() == null) {
            hasError = true;
            this.operatorsCanBreakReservationRulesSelect.setError(true);
        }

        const operatorMayEditOrders = this.operatorsMayEditOrdersSwitch.getValue();
        if (operatorMayEditOrders) {
            if (!this.editOrderUpToFulfillmentTimeTimePicker.validateInput()) {
                hasError = true;
            }
        }

        const endUserMayCancelOrders = this.endUsersMayCancelOrdersSwitch.getValue();
        if (endUserMayCancelOrders) {
            if (!this.cancelOrderUpToFulfillmentTimeTimePicker.validateInput()) {
                hasError = true;
            }
        }

        if (hasError) {
            return false;
        }

        //TODO: IMPROVE VALIDATION
        const val = this.getValue();
        this.$element.find(".is-invalid").removeClass("is-invalid");

        if (val.defaultCapacityPerFulfilmentTime != null && val.defaultCapacityPerFulfilmentTime <= 0) {
            hasError = true;
            this.defaultCapacityPerFulfilmentNumberInput.setError(true);
        }

        if (val.name == null || val.name.trim().length == 0) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        } else {
            this.$nameField.removeClass("is-invalid");
        }

        this.fromTimePicker.setError(false);
        this.untilTimePicker.setError(false);

        if (!val.wholeDay) {
            if (val.from == null) {
                this.fromTimePicker.setError(true);
                hasError = true;
            }

            if (val.until == null) {
                this.untilTimePicker.setError(true);
                hasError = true;
            }
        }

        if (
            val.from !== undefined &&
            val.until !== undefined &&
            DateTime.secondsOfTimeSpan(val.from) >= DateTime.secondsOfTimeSpan(val.until)
        ) {
            this.untilTimePicker.setError(true);
            hasError = true;
        }

        if (hasError) {
            this.scrollToFirstErrorField();
            return false;
        }

        return val;
    }

    public getValue(): IScheduleShift {
        const isWholeDay = this.$wholeDayCheck.is(":checked");
        const operatorMayEditOrders = this.operatorsMayEditOrdersSwitch.getValue();
        const endUsersMayCancelOrders = this.endUsersMayCancelOrdersSwitch.getValue();

        return {
            autoAcceptUnassignedReservations: this.autoAcceptUnassignedReservationsSwitch.getValue(),
            capacityAdjustmentPercentage: parseInt(this.capacityAdjustmentPercentageSlider.get()),
            expectedLostSeatRatio: parseInt(this.expectedLostSeatRatioSlider.get()) / 10,
            operatorsCanBreakReservationRules: parseInt(this.operatorsCanBreakReservationRulesSelect.getValue()!),
            overbookingAboveThreshold: this.overbookingAboveThresholdField.getValue(),
            operatorsMustAcceptOrDeclineReservation: parseInt(
                this.operatorsMustAcceptOrDeclineReservationSelect.getValue()!
            ),
            id: this.id,
            name: this.$nameField.val() ?? "",
            weekdays: this.weekdaySelect.getValues().map((c) => Weekday[c]),
            wholeDay: isWholeDay,
            from: isWholeDay ? undefined : this.fromTimePicker.getValue(),
            until: isWholeDay ? undefined : this.untilTimePicker.getValue(),
            preorderTimeInSeconds: this.preOrderTimePicker.getSeconds()!,
            fulfilmentTimeInterval: this.fulfilmentTimeIntervalTimePicker.getValue()!,
            defaultCapacityPerFulfilmentTime: this.defaultCapacityPerFulfilmentNumberInput.getValue(),
            editOrderByOperatorUpToFulfillmentTime: operatorMayEditOrders
                ? this.editOrderUpToFulfillmentTimeTimePicker.getValidatedDuration()
                : undefined,
            cancelOrderByEndUserUpToFulfillmentTime: endUsersMayCancelOrders
                ? this.cancelOrderUpToFulfillmentTimeTimePicker.getValidatedDuration()
                : undefined,
            preorderRefundabilityType: parseInt(this.refundabilityTypeSelect.getValue()!),
            excludedTablesOrCombinations: this.readExcludedTablesOrCombinations(),
            messageToUserAfterPendingOperatorReservation:
                this.messageToUserAfterPendingOperatorReservationField.getValues(),
        };
    }

    public getValueToClone(): IScheduleShift {
        const isWholeDay = this.$wholeDayCheck.is(":checked");
        const operatorMayEditOrders = this.operatorsMayEditOrdersSwitch.getValue();
        const endUsersMayCancelOrders = this.endUsersMayCancelOrdersSwitch.getValue();

        return {
            autoAcceptUnassignedReservations: this.autoAcceptUnassignedReservationsSwitch.getValue(),
            capacityAdjustmentPercentage: parseInt(this.capacityAdjustmentPercentageSlider.get()),
            expectedLostSeatRatio: parseInt(this.expectedLostSeatRatioSlider.get()) / 10,
            operatorsCanBreakReservationRules: parseInt(this.operatorsCanBreakReservationRulesSelect.getValue()!),
            operatorsMustAcceptOrDeclineReservation: parseInt(
                this.operatorsMustAcceptOrDeclineReservationSelect.getValue()!
            ),
            overbookingAboveThreshold: this.overbookingAboveThresholdField.getValue(),
            id: uuidv4(),
            name: this.$nameField.val() ?? "",
            weekdays: this.weekdaySelect.getValues().map((c) => Number(c)),
            wholeDay: isWholeDay,
            from: isWholeDay ? undefined : this.fromTimePicker.getValue(),
            until: isWholeDay ? undefined : this.untilTimePicker.getValue(),
            preorderTimeInSeconds: this.preOrderTimePicker.getSeconds()!,
            fulfilmentTimeInterval: this.fulfilmentTimeIntervalTimePicker.getValue()!,
            defaultCapacityPerFulfilmentTime: this.defaultCapacityPerFulfilmentNumberInput.getValue(),
            editOrderByOperatorUpToFulfillmentTime: operatorMayEditOrders
                ? this.editOrderUpToFulfillmentTimeTimePicker.getValidatedDuration()
                : undefined,
            cancelOrderByEndUserUpToFulfillmentTime: endUsersMayCancelOrders
                ? this.cancelOrderUpToFulfillmentTimeTimePicker.getValidatedDuration()
                : undefined,
            preorderRefundabilityType: parseInt(this.refundabilityTypeSelect.getValue()!),
            excludedTablesOrCombinations: this.readExcludedTablesOrCombinations(),
            messageToUserAfterPendingOperatorReservation:
                this.messageToUserAfterPendingOperatorReservationField.getValues(),
        };
    }

    private readExcludedTablesOrCombinations(): IExcludedTableOrCombination[] {
        return this.excludedTablesMultiselectList.getValue().map((c) => {
            const selectedCombination = this.currentTablesOrCombinationsToExclude?.combinations?.find(
                (co) => co.combinationId === c
            );
            const selectedTable = this.currentTablesOrCombinationsToExclude?.tables?.find((co) => co.tableId === c);

            if (selectedCombination != null) {
                return {
                    id: selectedCombination.combinationId,
                    isCombination: true,
                };
            }

            if (selectedTable != null) {
                return {
                    id: selectedTable.tableId,
                    isCombination: false,
                };
            }

            throw Error("Selected table or combination was not found: " + c);
        });
    }

    private scrollToFirstErrorField() {
        const invalidFields = this.$element.find(".is-invalid");

        if (invalidFields.length) {
            DomUtils.scrollTo(invalidFields.first());
        }
    }
}
